'use client';

import React from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { FileText, ChevronRight } from 'lucide-react';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using The Private World of Luxury platform ("Platform"), available at theprivateworld.app, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not access or use the Platform.

These Terms constitute a legally binding agreement between you and Silvergate Royals. We reserve the right to update these Terms at any time, and your continued use of the Platform constitutes acceptance of any changes.`,
  },
  {
    id: 'description',
    title: '2. Description of Service',
    content: `The Private World of Luxury is a premium editorial discovery platform providing curated content about extraordinary luxury experiences, places, objects, people, and markets. The Platform offers:

• **Free Access**: Limited browsing of editorial content and discovery features.
• **Private Membership**: Full access to all editorial content, Intelligence Desk reports, curated collections, My Library, and exclusive member features.
• **Editorial Content**: Articles, reports, and curated discoveries across luxury categories.

We reserve the right to modify, suspend, or discontinue any aspect of the Platform at any time.`,
  },
  {
    id: 'accounts',
    title: '3. User Accounts',
    content: `To access certain features, you must create an account. You agree to:

• Provide accurate, current, and complete information during registration
• Maintain and promptly update your account information
• Keep your password confidential and not share it with any third party
• Notify us immediately of any unauthorised use of your account
• Accept responsibility for all activities that occur under your account

You must be at least 18 years of age to create an account. We reserve the right to suspend or terminate accounts that violate these Terms.`,
  },
  {
    id: 'membership',
    title: '4. Membership & Subscriptions',
    content: `**Membership Plans**: We offer annual membership plans as described on our Subscription page. Pricing is displayed in the applicable currency at the time of purchase.

**Billing**: Membership fees are billed in advance on an annual basis. By subscribing, you authorise us to charge your payment method for the applicable fees.

**Renewal**: Memberships automatically renew unless cancelled at least 24 hours before the renewal date. You can manage your subscription through your Subscriber Dashboard.

**Refunds**: We offer a 7-day refund policy for new memberships. After 7 days, membership fees are non-refundable except where required by applicable law.

**Price Changes**: We may change membership pricing with 30 days' notice. Continued use after the effective date constitutes acceptance of the new pricing.`,
  },
  {
    id: 'intellectual-property',title: '5. Intellectual Property',
    content: `All content on the Platform — including articles, editorial reports, photographs, graphics, logos, and the Platform design — is the exclusive property of Silvergate Royals or its content licensors and is protected by copyright, trademark, and other intellectual property laws.

You may not:
• Reproduce, distribute, or republish any Platform content without prior written permission
• Use our trademarks, logos, or brand elements without authorisation
• Scrape, crawl, or systematically extract content from the Platform
• Create derivative works based on our content

Personal, non-commercial use of content for reference purposes is permitted provided attribution is given.`,
  },
  {
    id: 'user-conduct',title: '6. Acceptable Use',
    content: `You agree not to use the Platform to:

• Violate any applicable laws or regulations
• Infringe the intellectual property rights of others
• Transmit any harmful, offensive, or disruptive content
• Attempt to gain unauthorised access to any part of the Platform or its systems
• Interfere with or disrupt the integrity or performance of the Platform
• Use automated tools to access or interact with the Platform without permission
• Impersonate any person or entity
• Engage in any activity that could damage our reputation or business

We reserve the right to terminate access for any user who violates these provisions.`,
  },
  {
    id: 'third-party',title: '7. Third-Party Links & Content',
    content: `The Platform may contain links to third-party websites or reference third-party content. These links are provided for convenience only. We do not endorse, control, or accept responsibility for the content, privacy practices, or availability of third-party sites. Your interactions with third-party sites are governed by their own terms and policies.`,
  },
  {
    id: 'disclaimers',title: '8. Disclaimers',
    content: `The Platform and its content are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. We do not warrant that:

• The Platform will be uninterrupted, error-free, or secure
• Any content is accurate, complete, or current
• The Platform will meet your specific requirements

Editorial content is for informational and entertainment purposes only and does not constitute financial, investment, legal, or professional advice.`,
  },
  {
    id: 'limitation',title: '9. Limitation of Liability',
    content: `To the fullest extent permitted by law, Silvergate Royals shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Platform, even if we have been advised of the possibility of such damages.

Our total liability to you for any claims arising from these Terms or your use of the Platform shall not exceed the amount you paid us in the 12 months preceding the claim.`,
  },
  {
    id: 'termination',title: '10. Termination',
    content: `We may suspend or terminate your access to the Platform at our sole discretion, with or without notice, for conduct that we believe violates these Terms or is harmful to other users, us, third parties, or the Platform.

You may terminate your account at any time through your account settings. Upon termination, your right to use the Platform ceases immediately. Provisions that by their nature should survive termination will remain in effect.`,
  },
  {
    id: 'governing-law',title: '11. Governing Law',
    content: `These Terms are governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Mumbai, India. If you are accessing the Platform from outside India, you are responsible for compliance with local laws.`,
  },
  {
    id: 'contact',title: '12. Contact',
    content: `For questions about these Terms of Service, please contact us:

**Silvergate Royals**
Registered Address: New York, United States
Email: legal@theprivateworld.app
Website: theprivateworld.app`,
  },
];

export default function TermsOfServicePage() {
  return (
    <AppLayout currentPath="/terms-of-service">
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b border-[var(--border)] bg-[var(--secondary)]">
          <div className="max-w-3xl mx-auto px-6 py-10">
            <div className="flex items-center gap-2 text-xs text-[var(--slate-muted)] mb-4">
              <Link href="/" className="hover:text-[var(--gold)] transition-colors">Home</Link>
              <ChevronRight size={12} />
              <span>Terms of Service</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/20 flex items-center justify-center">
                <FileText size={16} className="text-[var(--gold)]" />
              </div>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[var(--gold)]">Legal</span>
            </div>
            <h1 className="font-editorial text-3xl font-semibold text-[var(--foreground)] mb-2">Terms of Service</h1>
            <p className="text-sm text-[var(--slate-muted)]">Effective date: 1 September 2025 &nbsp;·&nbsp; Last updated: 1 September 2025</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-10">
          {/* Quick Nav */}
          <div className="mb-10 p-5 bg-[var(--secondary)] border border-[var(--border)] rounded">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--slate-muted)] mb-3">Contents</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {sections?.map((s) => (
                <a
                  key={s?.id}
                  href={`#${s?.id}`}
                  className="text-xs text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors py-0.5"
                >
                  {s?.title}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections?.map((section) => (
              <section key={section?.id} id={section?.id} className="scroll-mt-6">
                <h2 className="font-editorial text-lg font-semibold text-[var(--foreground)] mb-3 pb-2 border-b border-[var(--border)]">
                  {section?.title}
                </h2>
                <div className="text-sm text-[var(--slate-muted)] leading-relaxed">
                  {section?.content?.split('\n')?.map((line, i) => {
                    if (line?.startsWith('• **')) {
                      const match = line?.match(/^• \*\*(.+?)\*\*: (.+)$/);
                      if (match) {
                        return (
                          <p key={i} className="mb-1.5 pl-3">
                            <span className="font-semibold text-[var(--foreground)]">{match?.[1]}:</span>{' '}
                            {match?.[2]}
                          </p>
                        );
                      }
                    }
                    if (line?.startsWith('• ')) {
                      return <p key={i} className="mb-1.5 pl-3">{line}</p>;
                    }
                    if (line?.startsWith('**') && line?.endsWith('**')) {
                      return <p key={i} className="font-semibold text-[var(--foreground)] mt-3 mb-1">{line?.replace(/\*\*/g, '')}</p>;
                    }
                    if (line === '') return <div key={i} className="h-2" />;
                    return <p key={i} className="mb-2">{line}</p>;
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-14 pt-6 border-t border-[var(--border)] flex flex-wrap gap-4 text-xs text-[var(--slate-muted)]">
            <Link href="/privacy-policy" className="hover:text-[var(--gold)] transition-colors">Privacy Policy</Link>
            <Link href="/cookie-policy" className="hover:text-[var(--gold)] transition-colors">Cookie Policy</Link>
            <Link href="/" className="hover:text-[var(--gold)] transition-colors">Back to Home</Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
